import React, { useState, useEffect } from 'react';
import Card from 'components/card';
import Widget from 'components/widget/Widget';
import { 
  MdAttachMoney, 
  MdTrendingUp, 
  MdUpdate, 
  MdAdd, 
  MdEdit, 
  MdClose, 
  MdWarning, 
  MdCheckCircle,
  MdBarChart,
  MdTimeline,
  MdGroups,
  MdLocalShipping,
  MdVisibility,
  MdExpandMore,
  MdExpandLess,
  MdBusiness
} from 'react-icons/md';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Area } from 'recharts';
import { jwtDecode } from 'jwt-decode';
import { tokenManager } from 'utils/tokenManager';



export function PriceInput() {
  // State management
  const [prices, setPrices] = useState({});
  const [timestamps, setTimestamps] = useState({});
  const [historicalData, setHistoricalData] = useState({});
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [selectedTrendAssociation, setSelectedTrendAssociation] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState(new Set());
  const [branchData, setBranchData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Fetch branch data and prices from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchLoading(true);
        setFetchError(null);
        
        // Get authorization header
        const authHeader = tokenManager.getAuthHeader();
        if (!authHeader) {
          throw new Error('Authentication token not found. Please login again.');
        }

        // Fetch branch data
        const branchResponse = await fetch('https://usibras-web-backend.onrender.com/api/branch-summary', {
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          }
        });
        if (!branchResponse.ok) {
          throw new Error(`HTTP error! status: ${branchResponse.status}`);
        }
        
        const branchResult = await branchResponse.json();
        

        
        // Fetch latest prices
        const pricesResponse = await fetch('https://usibras-web-backend.onrender.com/api/prices/latest-by-branch', {
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          }
        });
        if (!pricesResponse.ok) {
          throw new Error(`HTTP error! status: ${pricesResponse.status}`);
        }
        
        const pricesResult = await pricesResponse.json();
        
        // Transform prices data to match component structure
        const pricesData = {};
        const timestampsData = {};
        pricesResult.data.forEach(price => {
          pricesData[price.branch_name] = parseFloat(price.price_per_kg);
          timestampsData[price.branch_name] = price.updated_at;
        });
        
        setPrices(pricesData);
        setTimestamps(timestampsData);
        
        // Fetch historical price data for trends
        const historicalResponse = await fetch('https://usibras-web-backend.onrender.com/api/prices', {
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          }
        });
        if (!historicalResponse.ok) {
          throw new Error(`HTTP error! status: ${historicalResponse.status}`);
        }
        
        const historicalResult = await historicalResponse.json();
        
        // Process historical data for trends
        const processedHistoricalData = {};
        historicalResult.data.forEach(price => {
          const branchName = price.branch_name;
          const date = new Date(price.created_at);
          const month = date.toLocaleDateString('en-GB', { month: 'short' });
          
          if (!processedHistoricalData[branchName]) {
            processedHistoricalData[branchName] = [];
          }
          
          processedHistoricalData[branchName].push({
            month: month,
            price: parseFloat(price.price_per_kg),
            volume: 1000, // Default volume since API doesn't provide it
            date: date,
            id: price.id
          });
        });
        
        // Sort each branch's data by date (newest first)
        Object.keys(processedHistoricalData).forEach(branch => {
          processedHistoricalData[branch].sort((a, b) => b.date - a.date);
        });
        
        setHistoricalData(processedHistoricalData);
        
        // Merge price data with branch data to show all branches with prices
        const allBranches = new Set([
          ...branchResult.data.map(branch => branch.branch_name),
          ...pricesResult.data.map(price => price.branch_name)
        ]);
        
        const mergedData = Array.from(allBranches).map(branchName => {
          const branchData = branchResult.data.find(b => b.branch_name === branchName);
          
          if (branchData) {
            // Branch exists in branch data
            return {
              branch: branchName,
              associations: branchData.associations.map(assoc => ({
                id: `${branchName}-${assoc.association_name}`,
                name: assoc.association_name,
                memberCount: assoc.farmer_count,
                lastUpdated: new Date().toISOString().split('T')[0],
                status: 'active'
              }))
            };
          } else {
            // Branch only exists in price data - create placeholder
            return {
              branch: branchName,
              associations: [] // 0 associations
            };
          }
        });
        
        setBranchData(mergedData);
        
        // Set default selected trend association to first branch if available
        if (mergedData.length > 0 && !selectedTrendAssociation) {
          setSelectedTrendAssociation(mergedData[0].branch);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setFetchError(error.message);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate summary metrics
  const allAssociations = branchData.flatMap(branch => branch.associations);
  const summaryMetrics = {
    totalBranches: branchData.length,
    totalAssociations: allAssociations.length,
    totalMembers: allAssociations.reduce((acc, assoc) => acc + assoc.memberCount, 0),
    averagePrice: Object.values(prices).length > 0 
      ? (Object.values(prices).reduce((a, b) => a + b, 0) / Object.values(prices).length).toFixed(2)
      : '0.00'
  };

  // Get username from localStorage
  const getUsername = () => {
    const token = localStorage.getItem('usibras_login');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        return decodedToken.username;
      } catch (error) {
        console.error('Error decoding token:', error);
        return 'admin_user';
      }
    }
    return 'admin_user';
  };

  // Handle price update
  const handleUpdatePrice = async () => {
    if (!newPrice || !selectedAssociation) return;
    
    setLoading(true);
    try {
      const username = getUsername();
      
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      const response = await fetch('https://usibras-web-backend.onrender.com/api/prices', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          branch_name: selectedAssociation.id,
          price_per_kg: parseFloat(newPrice),
          created_by: username
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Price update response:', result);
      
      // Update local state with new price
      setPrices(prev => ({ 
        ...prev, 
        [selectedAssociation.id]: parseFloat(newPrice) 
      }));
      
      // Update timestamp with current time
      setTimestamps(prev => ({
        ...prev,
        [selectedAssociation.id]: new Date().toISOString()
      }));
      
      setNewPrice('');
      setIsUpdateModalOpen(false);
      setShowSuccess(true);
      
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating price:', error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  // Get trend data for selected branch
  const getTrendData = (branchName) => {
    const data = historicalData[branchName] || [];
    // Return the last 7 entries for the chart (or all if less than 7)
    return data.slice(0, 7).reverse(); // Reverse to show oldest to newest
  };

  // Calculate price change percentage
  const getPriceChange = (branchName) => {
    const data = historicalData[branchName];
    if (!data || data.length < 2) return '+0%';
    
    const current = data[0].price; // Most recent price
    const previous = data[1].price; // Second most recent price
    const change = ((current - previous) / previous * 100).toFixed(1);
    
    return `${change > 0 ? '+' : ''}${change}%`;
  };

  // Toggle branch expansion - only one branch open at a time
  const toggleBranch = (branchName) => {
    setExpandedBranches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(branchName)) {
        // If clicking the same branch, close it
        newSet.delete(branchName);
      } else {
        // If clicking a different branch, close all others and open this one
        newSet.clear();
        newSet.add(branchName);
      }
      return newSet;
    });
  };

  // Success notification component
  const SuccessNotification = () => (
    <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
      <MdCheckCircle className="text-xl" />
      <span>Price updated successfully!</span>
    </div>
  );

  // Loading state
  if (fetchLoading) {
    return (
      <div className="mt-3">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading branch data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="mt-3">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-red-100 p-4 rounded-lg">
              <MdWarning className="text-red-600" size={32} />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{fetchError}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex flex-col gap-5">
        {/* Success Notification */}
        {showSuccess && <SuccessNotification />}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-3 rounded-lg">
              <MdAttachMoney className="text-green-600" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Cashew Market Price</h2>
              <p className="text-sm text-gray-600">Manage and monitor cashew pricing across all associations</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Widget
            icon={<MdBusiness className="text-blue-600 text-2xl" />}
            title="Total Branch"
            subtitle={summaryMetrics.totalBranches}
          />
          <Widget
            icon={<MdGroups className="text-green-600 text-2xl" />}
            title="Total Associations"
            subtitle={summaryMetrics.totalAssociations}
          />
          <Widget
            icon={<MdGroups className="text-purple-600 text-2xl" />}
            title="Total Farmers"
            subtitle={`${summaryMetrics.totalMembers.toLocaleString()} farmers`}
          />
          <Widget
            icon={<MdBarChart className="text-orange-600 text-2xl" />}
            title="Average Price"
            subtitle={`GH₵${summaryMetrics.averagePrice}`}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6">
                    {/* Associations Table */}
          <Card extra="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-xl">
                  <MdGroups className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Cashew Market Pricing</h3>
                  <p className="text-gray-500">Manage and monitor pricing across all cashew associations</p>
                </div>
              </div>
              
              <button
                onClick={() => setIsUpdateModalOpen(true)}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-all duration-200"
              >
                <MdAttachMoney className="text-lg" />
                Update Branch Price
              </button>
            </div>

            <div className="overflow-x-auto">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                      <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wider">Branch</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wider">Members</th>
                      <th className="text-right py-4 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wider">Current Price</th>
                      <th className="text-center py-4 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wider">Time</th>
                      <th className="text-center py-4 px-6 font-semibold text-gray-700 text-sm uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                                     <tbody className="divide-y divide-gray-100">
                     {branchData.map((branchData, branchIndex) => {
                       const isExpanded = expandedBranches.has(branchData.branch);
                       const totalMembers = branchData.associations.reduce((sum, assoc) => sum + assoc.memberCount, 0);
                       
                       return (
                         <React.Fragment key={branchData.branch}>
                           {/* Branch Row */}
                           <tr className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group cursor-pointer border-l-4 border-transparent hover:border-blue-500" onClick={() => toggleBranch(branchData.branch)}>
                             <td className="py-5 px-6">
                               <div className="flex items-center gap-3">
                                 <div className="bg-blue-500 p-3 rounded-lg">
                                   <MdBusiness className="text-white" size={20} />
                                 </div>
                                 <div className="flex-1">
                                   <span className="text-gray-800 font-bold text-lg">{branchData.branch}</span>
                                   <div className="flex items-center gap-2 mt-1">
                                     <span className="text-xs text-gray-500">Branch</span>
                                     <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                       {branchData.associations.length} associations
                                     </span>
                                   </div>
                                 </div>
                                 <div className={`p-2 rounded-lg transition-all duration-200 ${
                                   isExpanded 
                                     ? 'bg-blue-100 text-blue-600' 
                                     : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                 }`}>
                                   {isExpanded ? (
                                     <MdExpandLess className="text-xl" />
                                   ) : (
                                     <MdExpandMore className="text-xl" />
                                   )}
                                 </div>
                               </div>
                             </td>
                             <td className="py-5 px-6">
                               <div className="flex items-center gap-3">
                                                                    <div className="bg-green-500 p-2 rounded-lg">
                                     <MdGroups className="text-white" size={16} />
                                   </div>
                                 <div>
                                   <div className="text-sm text-gray-600">Total Members</div>
                                   <div className="text-lg font-semibold text-gray-800">{totalMembers.toLocaleString()}</div>
                                   <div className="text-xs text-gray-500">farmers</div>
                                 </div>
                               </div>
                             </td>
                             <td className="py-5 px-6 text-right">
                               <div className="flex flex-col items-end">
                                 <div className="text-2xl font-bold text-gray-800">
                                   GH₵{prices[branchData.branch] ? prices[branchData.branch].toFixed(2) : '0.00'}
                                 </div>
                                 <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full mt-1">per kg</div>
                               </div>
                             </td>
                             <td className="py-5 px-6 text-center">
                               <div className="flex flex-col items-center">
                                 <div className="text-sm font-medium text-gray-800">
                                   {timestamps[branchData.branch] ? 
                                     new Date(timestamps[branchData.branch]).toLocaleDateString('en-GB', { 
                                       day: 'numeric', 
                                       month: 'short', 
                                       year: 'numeric'
                                     }) : 
                                     'N/A'
                                   }
                                 </div>
                                 <div className="text-xs text-gray-500">
                                   {timestamps[branchData.branch] ? 
                                     new Date(timestamps[branchData.branch]).toLocaleTimeString('en-GB', { 
                                       hour: '2-digit', 
                                       minute: '2-digit'
                                     }) : 
                                     'N/A'
                                   }
                                 </div>
                               </div>
                             </td>
                             <td className="py-5 px-6 text-center">
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setSelectedAssociation({ id: branchData.branch, name: branchData.branch, type: 'branch' });
                                   setIsUpdateModalOpen(true);
                                 }}
                                 className="p-3 text-gray-400 hover:text-blue-600 transition-all duration-200 rounded-lg hover:bg-blue-50 border border-gray-200 hover:border-blue-300"
                                 title="Change Price"
                               >
                                 <MdEdit size={18} />
                               </button>
                             </td>
                           </tr>
                           
                           {/* Expanded Associations */}
                           {isExpanded && branchData.associations.map((association, assocIndex) => {
                             return (
                               <tr key={`${branchData.branch}-${association.id}`} className="bg-gradient-to-r from-gray-50 to-blue-50 hover:from-gray-100 hover:to-blue-100 transition-all duration-200 border-l-4 border-blue-200">
                                 <td className="py-4 px-6 pl-16">
                                   <div className="flex items-center gap-3">
                                     <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                     <div>
                                       <h4 className="font-bold text-gray-800 text-base">{association.name}</h4>
                                     </div>
                                   </div>
                                 </td>
                                 <td className="py-4 px-6">
                                   <div className="flex items-center gap-3">
                                     <div className="bg-orange-500 p-2 rounded-lg">
                                       <MdGroups className="text-white" size={16} />
                                     </div>
                                     <div>
                                       <div className="text-sm text-gray-600">Members</div>
                                       <div className="text-lg font-bold text-gray-800">{association.memberCount.toLocaleString()}</div>
                                       <div className="text-xs text-gray-500">farmers</div>
                                     </div>
                                   </div>
                                 </td>
                                 <td className="py-4 px-6 text-right">
                                   <div className="text-gray-400">
                                     <span className="text-xs">-</span>
                                   </div>
                                 </td>
                                 <td className="py-4 px-6 text-center">
                                   <div className="text-gray-400">
                                     <span className="text-xs">-</span>
                                   </div>
                                 </td>
                                 <td className="py-4 px-6 text-center">
                                   <div className="text-gray-400">
                                     <span className="text-xs">-</span>
                                   </div>
                                 </td>
                               </tr>
                             );
                           })}
                         </React.Fragment>
                       );
                     })}
                   </tbody>
                </table>
              </div>
            </div>
            

          </Card>

          {/* Price Trends Chart - Full Width */}
          <Card extra="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-xl">
                  <MdTrendingUp className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Price Trends</h3>
                  <p className="text-gray-500">Track pricing patterns and market movements</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">Price Trend</span>
                </div>
            <select
                  className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 shadow-sm"
                  value={selectedTrendAssociation}
                  onChange={(e) => setSelectedTrendAssociation(e.target.value)}
                >
                  {branchData.map(branch => (
                    <option key={branch.branch} value={branch.branch}>{branch.branch}</option>
                  ))}
                </select>
          </div>
            </div>

            {/* Current Price Display */}
            <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Current Price</p>
                  <p className="text-3xl font-bold text-green-800">
                    GH₵{getTrendData(selectedTrendAssociation)[getTrendData(selectedTrendAssociation).length - 1]?.price || prices[selectedTrendAssociation] || 0}
                  </p>
                  <p className="text-sm text-green-600">per bag (kg)</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-green-700 mb-1">Price Change</p>
                  <p className={`text-2xl font-bold ${getPriceChange(selectedTrendAssociation).includes('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {getPriceChange(selectedTrendAssociation)}
                  </p>
                  <p className="text-sm text-green-600">vs last month</p>
                </div>
              </div>
              
              {/* Latest Update Section */}
              <div className="mt-6 pt-6 border-t border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white p-2 rounded-lg shadow-sm">
                      <MdUpdate className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-700">Latest Update</p>
                      <p className="text-sm text-green-600">
                        {new Date().toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-700">Updated by</p>
                    <p className="text-sm text-green-600">Admin Manager</p>
                  </div>
                </div>
                
                {/* Update Status Indicator */}
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 font-medium">Live pricing data</span>
                </div>
              </div>
            </div>

            {/* Enhanced Chart */}
            <div className="relative">
              <div className="h-96 bg-white rounded-lg p-6 border border-gray-200">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={getTrendData(selectedTrendAssociation)}>
                    <defs>
                      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      domain={[1.5, 3.0]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickFormatter={(value) => `GH₵${value}`}
                    />
                                          <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value) => [`GH₵${value}`, 'Price']}
                        labelFormatter={(label) => `${label} 2024`}
                      />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#10b981" 
                      strokeWidth={4}
                      dot={{ 
                        fill: '#10b981', 
                        stroke: '#ffffff', 
                        strokeWidth: 3, 
                        r: 6,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }}
                      activeDot={{ 
                        r: 8, 
                        stroke: '#10b981', 
                        strokeWidth: 2,
                        fill: '#ffffff',
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="price" 
                      stroke="url(#priceGradient)" 
                      fill="url(#priceGradient)" 
                      strokeWidth={0}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Chart Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <MdTrendingUp className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Highest Price</p>
                      <p className="text-lg font-bold text-gray-800">
                        GH₵{Math.max(...getTrendData(selectedTrendAssociation).map(d => d.price)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <MdTrendingUp className="text-red-600" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Lowest Price</p>
                      <p className="text-lg font-bold text-gray-800">
                        GH₵{Math.min(...getTrendData(selectedTrendAssociation).map(d => d.price)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <MdBarChart className="text-purple-600" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Average Price</p>
                      <p className="text-lg font-bold text-gray-800">
                        GH₵{(getTrendData(selectedTrendAssociation).reduce((sum, d) => sum + d.price, 0) / getTrendData(selectedTrendAssociation).length).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>



        {/* Update Price Modal */}
        {isUpdateModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setIsUpdateModalOpen(false)}></div>
            
            <div className="flex min-h-full items-center justify-center p-4">
              <Card extra="p-6 max-w-md w-full relative">
                <button
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MdClose size={24} />
                </button>

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-800">Update Branch Price</h3>
                  <p className="text-sm text-gray-500">Set new price for selected branch</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch
                    </label>
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-200"
                      value={selectedAssociation?.id || ''}
                      onChange={(e) => {
                        setSelectedAssociation({ id: e.target.value, name: e.target.value, type: 'branch' });
                      }}
                    >
                      <option value="">Select Branch</option>
                      <option value="Techiman">Techiman</option>
                      <option value="Nkoranza">Nkoranza</option>
                      <option value="Narelugu">Narelugu</option>
                      <option value="Banda">Banda</option>
                      <option value="Sampa">Sampa</option>
                      <option value="Wenchi">Wenchi</option>
                      <option value="Nsawkaw">Nsawkaw</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Branch Price
                    </label>
                    <div className="text-lg font-semibold text-gray-800">
                      GH₵{selectedAssociation ? 
                        (prices[selectedAssociation.id] ? prices[selectedAssociation.id].toFixed(2) : '0.00')
                        : '0.00'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Branch Price per kg
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-green-200"
                        placeholder="Enter new price"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">GH₵</span>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setIsUpdateModalOpen(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdatePrice}
                      disabled={!newPrice || !selectedAssociation || loading}
                      className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <MdUpdate className="text-lg" />
                      )}
                      {loading ? 'Updating...' : 'Update Branch Price'}
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PriceInput;